import { Icon } from '@iconify/react'
import { Badge } from '@/components/ui/Badge'
import { PROJECT_STATUS_LABELS, OCR_STATUS_LABELS, PROCUREMENT_METHOD_LABELS } from '@/lib/constants'
import { getStatusClass, getOcrStatusClass } from '@/lib/utils'
import type { ProjectStatus, OcrStatus } from '@/lib/pocketbase'

interface ProjectStatusBadgeProps {
  status: ProjectStatus
  className?: string
}

export function ProjectStatusBadge({ status, className }: ProjectStatusBadgeProps) {
  const labels = PROJECT_STATUS_LABELS[status]
  if (!labels) return null
  const label = labels.pro
  const colorClass = getStatusClass(status)

  const icons: Record<ProjectStatus, string> = {
    'tor-prep': 'solar:document-text-line-duotone',
    'purchase-request': 'solar:clipboard-list-line-duotone',
    'invitation-issued': 'solar:letter-opened-line-duotone',
    'award-approved': 'solar:cup-star-line-duotone',
    'contract-management': 'solar:document-medicine-line-duotone',
    'project-cancelled': 'solar:close-circle-line-duotone',
  }

  return (
    <Badge className={`${colorClass} ${className}`}>
      <Icon icon={icons[status]} className="w-3.5 h-3.5" />
      {label}
    </Badge>
  )
}

interface OcrStatusBadgeProps {
  status: OcrStatus
  className?: string
}

export function OcrStatusBadge({ status, className }: OcrStatusBadgeProps) {
  const colorClass = getOcrStatusClass(status)
  const statusConfig = OCR_STATUS_LABELS[status]
  if (!statusConfig) return null

  const icons: Record<OcrStatus, string> = {
    pending: 'solar:clock-circle-line-duotone',
    processing: 'solar:refresh-circle-line-duotone',
    done: 'solar:check-circle-line-duotone',
    failed: 'solar:danger-circle-line-duotone',
  }

  return (
    <Badge size="sm" className={`${colorClass} ${className}`}>
      <Icon
        icon={icons[status]}
        className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`}
      />
      {statusConfig.label}
    </Badge>
  )
}

interface ProcurementMethodBadgeProps {
  method: string
  className?: string
}

export function ProcurementMethodBadge({ method, className }: ProcurementMethodBadgeProps) {
  const methodIcons: Record<string, string> = {
    'e-bidding': 'solar:laptop-line-duotone',
    'e-market': 'solar:shop-line-duotone',
    specific: 'solar:target-line-duotone',
    'price-survey': 'solar:magnifer-line-duotone',
  }
  const label = PROCUREMENT_METHOD_LABELS[method] || method

  return (
    <Badge variant="secondary" className={className}>
      <Icon icon={methodIcons[method] || 'solar:document-line-duotone'} className="w-3.5 h-3.5" />
      {label}
    </Badge>
  )
}
